import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Loader2 } from 'lucide-react'

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    async function handleCallback() {
      const code = searchParams.get('code')
      const next = searchParams.get('next') || '/'
      const type = searchParams.get('type')

      if (!code) {
        navigate('/?auth=signin&error=auth_callback_error', { replace: true })
        return
      }

      const { error } = await supabase.auth.exchangeCodeForSession(code)

      if (error) {
        console.error('Auth callback error:', error)
        navigate('/?auth=signin&error=auth_callback_error', { replace: true })
        return
      }

      // Handle different callback types
      if (type === 'recovery') {
        navigate('/auth/reset-password', { replace: true })
        return
      }

      if (type === 'signup' || type === 'email_change') {
        const { data: { user } } = await supabase.auth.getUser()

        if (user) {
          // Create customer profile
          await supabase
            .from('customer_profiles')
            .upsert({
              id: user.id,
              email: user.email!,
              display_name: user.user_metadata?.display_name || null,
            })

          // Claim any guest orders via edge function
          try {
            await callEdgeFunction('claim-orders', {
              method: 'POST',
              withAuth: true,
            })
          } catch {
            // Non-critical
          }

          // Check if admin (use maybeSingle to avoid 406 error for non-admins)
          const { data: adminUser } = await supabase
            .from('admin_users')
            .select('id')
            .eq('id', user.id)
            .maybeSingle()

          if (adminUser) {
            navigate('/admin', { replace: true })
            return
          }
        }

        navigate('/account', { replace: true })
        return
      }

      // Default redirect
      navigate(next, { replace: true })
    }

    handleCallback()
  }, [searchParams, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  )
}
